import { useEffect, useState } from 'react';
import type React from 'react';
import { firstValueFrom } from 'rxjs';
import { BaseReportElement } from '../BaseReportElement';
import { translateEntityType } from '../../enums';
import { isReportTemplateEntityType } from '../../typeGuards';
import { renderDefinitionList } from '../renderHelpers';
import { formatStructurePath } from '../structurePath';
import { getBindingCatalog } from '../../runtime/dataServices/bindingCatalog';
import { parseDefinitions } from '../../runtime/definitionParsing';
import type {
    ContextForKind,
    ElementDefinition,
    ElementContentRenderResult,
    ElementSettings,
    ElementSettingsFormSchema,
    ResolveElementSettingsFormSchemaArgs,
    RenderContextKind,
    RenderRuntime,
} from '../types';
import type { StructurePath } from '../structurePath';
import { isRecord } from '../../../../../utils/typeGuards';

// Raw settings come from persisted/editor payloads; normalized settings are the
// internal invariant used by render/runtime logic after validation.
interface TemplateIncludeSettingsRaw extends ElementSettings {
    templateId?: unknown;
}

interface TemplateIncludeSettingsNormalized extends ElementSettings {
    templateId: string | null;
}

interface RenderedTemplateInclude {
    nodes: React.ReactNode[];
    ready: Promise<void>;
    hasRenderableContent: Promise<boolean>;
}

interface TemplateIncludeRendererProps {
    includePromise: Promise<RenderedTemplateInclude>;
    showEditor: boolean;
}

function toTemplateIncludeChoiceOptions(result: unknown): Array<[string, string]> {
    if (!Array.isArray(result)) {
        throw new Error('Expected template include options result to be an array.');
    }

    return result.reduce<Array<[string, string]>>((options, entry, index) => {
        if (!isRecord(entry)) {
            throw new Error(`Expected template include option at index ${index} to be an object.`);
        }

        const optionValue = entry.id;
        const optionLabel = entry.name;
        const optionMeta = entry.entityType;

        if (
            (typeof optionValue !== 'string' && typeof optionValue !== 'number') ||
            String(optionValue).trim().length === 0
        ) {
            throw new Error('Expected template include option "id" to be a non-empty value.');
        }

        if (
            (typeof optionLabel !== 'string' && typeof optionLabel !== 'number') ||
            String(optionLabel).trim().length === 0
        ) {
            throw new Error('Expected template include option "name" to be a non-empty value.');
        }

        if (
            optionMeta !== null &&
            typeof optionMeta !== 'undefined' &&
            !isReportTemplateEntityType(optionMeta)
        ) {
            throw new Error(
                'Expected template include option "entityType" to be a valid ReportTemplateEntityType value.'
            );
        }

        const normalizedLabel =
            optionMeta === null || typeof optionMeta === 'undefined'
                ? String(optionLabel)
                : `${String(optionLabel)} (${translateEntityType(optionMeta)})`;

        return [...options, [String(optionValue), normalizedLabel]];
    }, []);
}

function dedupeOptionsByValue(options: ReadonlyArray<[string, string]>): Array<[string, string]> {
    const seen = new Set<string>();

    return options.reduce<Array<[string, string]>>((uniqueOptions, option) => {
        const [value] = option;
        if (seen.has(value)) {
            return uniqueOptions;
        }

        seen.add(value);
        return [...uniqueOptions, option];
    }, []);
}

function renderIncludedTemplateDefinitions(
    definitions: ElementDefinition[],
    ctx: ContextForKind<RenderContextKind>,
    runtime: RenderRuntime,
    includePath: StructurePath
): RenderedTemplateInclude {
    const includeRuntime = runtime.getPreviewRuntime();
    const rendered = renderDefinitionList(
        definitions,
        ctx,
        includeRuntime,
        includePath,
        'template-root'
    );

    return {
        // Included templates render through preview runtime (no editor frame), so
        // keep parity with default element container constraints explicitly.
        nodes: rendered.nodes.map((node, index) => (
            <div key={`template-include-node-${index}`} className="relative max-w-full">
                {node}
            </div>
        )),
        ready: rendered.ready,
        hasRenderableContent: rendered.hasRenderableContent,
    };
}

function TemplateIncludeRenderer({
    includePromise,
    showEditor,
}: TemplateIncludeRendererProps): JSX.Element {
    const [nodes, setNodes] = useState<React.ReactNode[] | null>(null);
    const [renderError, setRenderError] = useState<Error | null>(null);

    useEffect(() => {
        let isActive = true;
        setNodes(null);
        setRenderError(null);

        void includePromise
            .then((renderedTemplate) => {
                if (!isActive) {
                    return;
                }

                setNodes(renderedTemplate.nodes);
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }

                setRenderError(error instanceof Error ? error : new Error(String(error)));
            });

        return () => {
            isActive = false;
        };
    }, [includePromise]);

    if (renderError) {
        if (!showEditor) {
            throw renderError;
        }

        return (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {renderError.message}
            </div>
        );
    }

    return <>{nodes}</>;
}

function renderTemplateIncludeElement(
    settings: TemplateIncludeSettingsNormalized,
    ctx: ContextForKind<RenderContextKind>,
    runtime: RenderRuntime
): ElementContentRenderResult {
    function isTemplateRootListPath(location: {
        listPath: StructurePath;
        slotKind: string;
    }): boolean {
        // Prefer insertion-slot semantics over raw list-path shape so root-level
        // repeat children (nested path, template-root slot) remain valid.
        if (location.slotKind === 'template-root') {
            return true;
        }

        return location.listPath[location.listPath.length - 1] === 'includedTemplate';
    }

    function formatTemplateListPath(listPath: StructurePath): string {
        return formatStructurePath(['structure', ...listPath]);
    }

    const location = runtime.getCurrentElementLocation();
    if (!location) {
        throw new Error(
            'Missing render location for element "template_include". Render through renderDefinitionList(...).'
        );
    }

    if (!isTemplateRootListPath(location)) {
        throw new Error(
            `Element "template_include" is only supported at template root, received "${formatTemplateListPath(
                location.listPath
            )}".`
        );
    }

    const { templateId } = settings;
    if (templateId === null) {
        const unconfiguredTemplateMessage = gettext('TEMPLATE_INCLUDE_NOT_CONFIGURED');

        if (!runtime.showEditor) {
            throw new Error(unconfiguredTemplateMessage);
        }

        return {
            node: (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    {unconfiguredTemplateMessage}
                </div>
            ),
            ready: Promise.resolve(),
            hasRenderableContent: Promise.resolve(true),
        };
    }

    const includeRuntime = runtime.getTemplateIncludeRuntime(templateId);
    const includePath = includeRuntime.getCurrentElementChildListPath('includedTemplate');
    const includePromise = firstValueFrom(
        includeRuntime.observeBinding(
            getBindingCatalog().toActiveDataBinding('template.resolve.structure', templateId)
        )
    )
        .then((linkValue) => parseDefinitions(linkValue, [`template_include(${templateId})`]))
        .then((definitions) =>
            renderIncludedTemplateDefinitions(definitions, ctx, includeRuntime, includePath)
        );

    const ready = includePromise
        .then((renderedTemplate) => renderedTemplate.ready)
        .catch((error) => {
            if (!runtime.showEditor) {
                throw error;
            }
            return undefined;
        });
    const hasRenderableContent = includePromise
        .then((renderedTemplate) => renderedTemplate.hasRenderableContent)
        .catch((error) => {
            if (!runtime.showEditor) {
                throw error;
            }

            // The editor renders the include's explicit error card, which is visible
            // authoring content and must keep its containing page on screen.
            return true;
        });

    return {
        node: (
            <TemplateIncludeRenderer
                includePromise={includePromise}
                showEditor={runtime.showEditor}
            />
        ),
        ready: Promise.all([ready, hasRenderableContent]).then(() => undefined),
        hasRenderableContent,
    };
}

export class TemplateIncludeElement extends BaseReportElement<
    RenderContextKind,
    TemplateIncludeSettingsRaw,
    TemplateIncludeSettingsNormalized
> {
    static readonly elementType = 'template_include';

    static readonly layout = { flow: 'flow' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_TEMPLATE_INCLUDE_LABEL'),
            supportedSlots: ['template-root'] as const,
            createDefinition: () => ({
                type: 'template_include',
                settings: {},
            }),
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        return {
            fields: [
                {
                    name: 'templateId',
                    label: gettext('TEMPLATE_ID'),
                    type: 'choice',
                    required: false,
                    options: [['', 'Select template...']],
                    validateOptions: false,
                    default: undefined,
                },
            ],
        };
    }

    static async resolveSettingsFormSchema({
        contextKind,
        runtimeDataServices,
        currentSettings,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        void currentSettings;

        const requestResult = await firstValueFrom(
            runtimeDataServices.observeBinding(
                getBindingCatalog().toActiveDataBinding('template.options.options', contextKind),
                {
                    elementPath: 'template_include.settings.schema',
                }
            )
        );
        const dynamicOptions = toTemplateIncludeChoiceOptions(requestResult);

        return {
            ...TemplateIncludeElement.settingsFormSchema,
            fields: TemplateIncludeElement.settingsFormSchema.fields.map((field) => {
                if (field.name !== 'templateId' || field.type !== 'choice') {
                    return field;
                }
                if (!field.options) {
                    throw new Error(
                        'Expected "template_include.templateId.options" to be defined.'
                    );
                }

                return {
                    ...field,
                    options: dedupeOptionsByValue([...dynamicOptions, ...field.options]),
                };
            }),
        };
    }

    protected normalizeSettings(
        settings: TemplateIncludeSettingsRaw
    ): TemplateIncludeSettingsNormalized {
        TemplateIncludeElement.validateSettings(settings);
        return {
            templateId:
                typeof settings.templateId === 'string' && settings.templateId.length > 0
                    ? settings.templateId
                    : null,
        };
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        return renderTemplateIncludeElement(this.settings, ctx, runtime);
    }
}
